/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Collection Grid
Version:  1.0.0
Updated:  2026-10-15 — First version.
Source:   DW-Component-Library/Kits/Collection-Grid

FUNCTION LOCKED. Theme-independent, no libraries. Drives
sections/dw-collection-grid.liquid:
- Filters, sorting, pills and page links load the section again through
  Shopify's Section Rendering API (?section_id=) and update the URL, so
  every state can be shared, reloaded and reached with Back/Forward.
- Open filter groups, scroll positions, the open layer and the focused
  control are kept across updates.
- "Load more" and infinite scroll append the next page's cards.
- Filter layer: X, Escape and the dimmed area close it; focus moves in
  and back; Tab stays inside while it is open.
Event after every update: "dw:collection-grid:updated" on the section
element (detail.sectionId), for apps that need to scan new cards.
---------------------------------------------------------
*/
(function () {
  if (window.customElements.get('dw-collection-grid')) return;

  var DESKTOP = '(min-width: 990px)';
  var PASS_PARAMS = ['q', 'type', 'options[prefix]'];
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  function headerOffset() {
    var offset = 0;
    var els = document.querySelectorAll('header, .header, sticky-header, header-component, .shopify-section-group-header-group');
    Array.prototype.forEach.call(els, function (el) {
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      var h = el.getBoundingClientRect().height;
      if (h > offset && h < window.innerHeight / 2) offset = h;
    });
    return offset;
  }

  class DWCollectionGrid extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.controller = null;
      this.loadingMore = false;
      this.lastFocus = null;
      this.priceTimer = null;
      this.observer = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onPop = this.onPop.bind(this);
      this.onMedia = this.onMedia.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('popstate', this.onPop);
      this.media = window.matchMedia(DESKTOP);
      if (this.media.addEventListener) this.media.addEventListener('change', this.onMedia);

      this.setup();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPop);
      if (this.media && this.media.removeEventListener) this.media.removeEventListener('change', this.onMedia);
      if (this.observer) this.observer.disconnect();
      if (this.isLayerOpen()) document.documentElement.style.overflow = '';
    }

    setup() {
      this.content = this.querySelector('[data-dw-cg-content]');
      this.live = this.querySelector('[data-dw-cg-live]');
      this.clampDescription();
      this.observeMore();
    }

    /* ---------- Layer ---------- */

    get layer() {
      return this.querySelector('[data-dw-cg-layer]');
    }

    isLayerOpen() {
      var layer = this.layer;
      return !!(layer && layer.classList.contains('dw-is-open'));
    }

    openLayer(trigger) {
      var layer = this.layer;
      if (!layer) return;
      this.lastFocus = trigger || document.activeElement;
      layer.classList.add('dw-is-open');
      layer.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      var close = layer.querySelector('.dw-cg__close');
      window.setTimeout(function () {
        if (close) close.focus({ preventScroll: true });
      }, 60);
    }

    closeLayer() {
      var layer = this.layer;
      if (!layer || !layer.classList.contains('dw-is-open')) return;
      layer.classList.remove('dw-is-open');
      layer.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      var target = this.lastFocus && this.lastFocus.isConnected ? this.lastFocus : null;
      if (!target) {
        target = Array.prototype.find.call(this.querySelectorAll('[data-dw-cg-open]'), function (b) {
          return b.offsetParent !== null;
        });
      }
      if (target && target.focus) target.focus({ preventScroll: true });
    }

    onMedia(event) {
      if (event.matches && this.dataset.layout !== 'drawer') this.closeLayer();
    }

    trapFocus(event) {
      var panel = this.layer && this.layer.querySelector('.dw-cg__panel');
      if (!panel) return;
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Dropdowns (desktop bar and sort) ---------- */

    closeDropdowns(except) {
      Array.prototype.forEach.call(this.querySelectorAll('details[data-dw-cg-dropdown][open]'), function (d) {
        if (d !== except) d.open = false;
      });
    }

    /* ---------- Events ---------- */

    onClick(event) {
      var t = event.target instanceof Element ? event.target : null;
      if (!t) return;

      var open = t.closest('[data-dw-cg-open]');
      if (open) {
        event.preventDefault();
        this.openLayer(open);
        return;
      }

      if (t.closest('[data-dw-cg-close]')) {
        event.preventDefault();
        this.closeLayer();
        return;
      }

      var summary = t.closest('summary');
      if (summary && summary.parentElement && summary.parentElement.hasAttribute('data-dw-cg-dropdown')) {
        this.closeDropdowns(summary.parentElement);
        return;
      }

      var link = t.closest('a[data-dw-cg-link]');
      if (link) {
        event.preventDefault();
        this.render(link.href);
        return;
      }

      var page = t.closest('[data-dw-cg-pages] a[href]');
      if (page) {
        event.preventDefault();
        this.render(page.href, { scroll: true });
        return;
      }

      if (t.closest('[data-dw-cg-load]')) {
        event.preventDefault();
        this.loadMore(true);
        return;
      }

      var toggle = t.closest('[data-dw-cg-desc-toggle]');
      if (toggle) {
        var desc = this.querySelector('[data-dw-cg-desc]');
        if (!desc) return;
        var clamped = desc.classList.toggle('dw-cg__desc--clamp');
        toggle.textContent = clamped ? toggle.dataset.more : toggle.dataset.less;
        toggle.setAttribute('aria-expanded', clamped ? 'false' : 'true');
      }
    }

    onDocClick(event) {
      var t = event.target instanceof Element ? event.target : null;
      var inside = t ? t.closest('details[data-dw-cg-dropdown]') : null;
      this.closeDropdowns(inside);
    }

    onChange(event) {
      var t = event.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (t.hasAttribute('data-dw-cg-sort')) {
        this.render(this.sortUrl(t.value));
        return;
      }
      var form = t.closest('form[data-dw-cg-form]');
      if (!form) return;
      if (t.hasAttribute('data-dw-cg-price')) window.clearTimeout(this.priceTimer);
      this.render(this.filterUrl(form));
    }

    onInput(event) {
      var t = event.target;
      if (!(t instanceof HTMLInputElement) || !t.hasAttribute('data-dw-cg-price')) return;
      var form = t.closest('form[data-dw-cg-form]');
      if (!form) return;
      var self = this;
      window.clearTimeout(this.priceTimer);
      this.priceTimer = window.setTimeout(function () {
        self.render(self.filterUrl(form));
      }, 900);
    }

    onSubmit(event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dw-cg-form')) return;
      event.preventDefault();
      window.clearTimeout(this.priceTimer);
      this.render(this.filterUrl(form));
    }

    onKey(event) {
      if (event.key === 'Escape') {
        if (this.isLayerOpen()) {
          this.closeLayer();
          return;
        }
        var openDrop = this.querySelector('details[data-dw-cg-dropdown][open]');
        if (openDrop) {
          openDrop.open = false;
          var s = openDrop.querySelector('summary');
          if (s) s.focus();
        }
        return;
      }
      if (event.key === 'Tab' && this.isLayerOpen()) this.trapFocus(event);
    }

    onPop() {
      var path = new URL(this.dataset.base, window.location.origin).pathname.replace(/\/$/, '');
      if (window.location.pathname.replace(/\/$/, '') !== path) return;
      this.render(window.location.href, { push: false, force: true });
    }

    /* ---------- URLs ---------- */

    filterUrl(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        if (typeof value !== 'string' || value.trim() === '') return;
        if (key.indexOf('filter.') !== 0 && PASS_PARAMS.indexOf(key) === -1) return;
        params.append(key, value.trim());
      });
      var sort = new URL(window.location.href).searchParams.get('sort_by');
      if (sort) params.set('sort_by', sort);
      var url = new URL(this.dataset.base, window.location.origin);
      url.search = params.toString();
      return url.toString();
    }

    sortUrl(value) {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      return url.toString();
    }

    /* ---------- Rendering ---------- */

    fetchContent(url) {
      if (this.controller) this.controller.abort();
      this.controller = 'AbortController' in window ? new AbortController() : null;
      var request = new URL(url, window.location.origin);
      request.searchParams.set('section_id', this.sectionId);
      var self = this;
      return fetch(request.toString(), {
        credentials: 'same-origin',
        signal: this.controller ? this.controller.signal : undefined
      })
        .then(function (r) {
          if (!r.ok) throw new Error('DW collection grid: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var root = doc.getElementById(self.id);
          var content = root ? root.querySelector('[data-dw-cg-content]') : null;
          if (!content) throw new Error('DW collection grid: section not found');
          return content;
        });
    }

    render(url, options) {
      var opts = options || {};
      var target = new URL(url, window.location.origin);
      target.searchParams.delete('section_id');
      if (!opts.force && target.toString() === window.location.href) return;

      var self = this;
      this.classList.add('dw-is-loading');
      this.setAttribute('aria-busy', 'true');
      this.fetchContent(target.toString())
        .then(function (content) {
          self.swap(content);
          if (opts.push !== false) history.pushState({ dwCollectionGrid: self.sectionId }, '', target.toString());
          if (opts.scroll) self.scrollToGrid();
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          window.location.href = target.toString();
        })
        .finally(function () {
          self.classList.remove('dw-is-loading');
          self.removeAttribute('aria-busy');
        });
    }

    swap(content) {
      var known = {};
      var open = {};
      Array.prototype.forEach.call(this.content.querySelectorAll('details[id]'), function (d) {
        known[d.id] = true;
        if (d.open) open[d.id] = true;
      });
      var scroll = {};
      Array.prototype.forEach.call(this.content.querySelectorAll('[data-dw-cg-scroll][id]'), function (el) {
        scroll[el.id] = el.scrollTop;
      });
      var layerOpen = this.isLayerOpen();
      var active = document.activeElement;
      var focusId = active && this.contains(active) && active.id ? active.id : null;

      var next = document.importNode(content, true);
      this.content.replaceWith(next);
      this.content = next;

      Array.prototype.forEach.call(next.querySelectorAll('details[id]'), function (d) {
        if (known[d.id]) d.open = !!open[d.id];
      });
      Array.prototype.forEach.call(next.querySelectorAll('[data-dw-cg-scroll][id]'), function (el) {
        if (scroll[el.id]) el.scrollTop = scroll[el.id];
      });

      var layer = this.layer;
      if (layerOpen && layer) {
        layer.classList.add('dw-cg__layer--instant', 'dw-is-open');
        layer.setAttribute('aria-hidden', 'false');
        void layer.offsetWidth;
        layer.classList.remove('dw-cg__layer--instant');
      } else if (layerOpen) {
        document.documentElement.style.overflow = '';
      }

      if (focusId) {
        var el = document.getElementById(focusId);
        if (el && this.contains(el)) el.focus({ preventScroll: true });
      } else if (layerOpen && layer) {
        var close = layer.querySelector('.dw-cg__close');
        if (close) close.focus({ preventScroll: true });
      }

      if (this.live) this.live.textContent = next.dataset.count || '';
      this.clampDescription();
      this.observeMore();
      this.dispatchEvent(new CustomEvent('dw:collection-grid:updated', { bubbles: true, detail: { sectionId: this.sectionId } }));
    }

    scrollToGrid() {
      var main = this.querySelector('[data-dw-cg-main]') || this;
      var top = main.getBoundingClientRect().top + window.scrollY - headerOffset() - 16;
      var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
    }

    /* ---------- Load more / infinite scroll ---------- */

    loadMore(fromClick) {
      var more = this.querySelector('[data-dw-cg-more]');
      if (!more || this.loadingMore) return;
      var list = this.querySelector('[data-dw-cg-list]');
      var button = more.querySelector('[data-dw-cg-load]');
      if (!list) return;

      var self = this;
      this.loadingMore = true;
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }
      this.fetchContent(more.dataset.next)
        .then(function (content) {
          var items = content.querySelectorAll('[data-dw-cg-item]');
          var firstNew = null;
          Array.prototype.forEach.call(items, function (item) {
            var node = document.importNode(item, true);
            list.appendChild(node);
            if (!firstNew) firstNew = node;
          });
          var nextMore = content.querySelector('[data-dw-cg-more]');
          if (nextMore) more.replaceWith(document.importNode(nextMore, true));
          else more.remove();
          if (fromClick && firstNew) {
            var focusTarget = firstNew.querySelector('a[href]');
            if (focusTarget) focusTarget.focus({ preventScroll: true });
          }
          self.observeMore();
          self.dispatchEvent(new CustomEvent('dw:collection-grid:updated', { bubbles: true, detail: { sectionId: self.sectionId } }));
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (button) {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          }
        })
        .finally(function () {
          self.loadingMore = false;
        });
    }

    observeMore() {
      if (this.observer) this.observer.disconnect();
      if (this.dataset.pagination !== 'infinite' || !('IntersectionObserver' in window)) return;
      var more = this.querySelector('[data-dw-cg-more]');
      if (!more) return;
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) self.loadMore(false);
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(more);
    }

    /* ---------- Description ---------- */

    clampDescription() {
      var desc = this.querySelector('[data-dw-cg-desc]');
      var toggle = this.querySelector('[data-dw-cg-desc-toggle]');
      if (!desc || !toggle) return;
      if (!desc.classList.contains('dw-cg__desc--clamp')) return;
      toggle.hidden = desc.scrollHeight <= desc.clientHeight + 1;
    }
  }

  customElements.define('dw-collection-grid', DWCollectionGrid);
})();
